import { useCallback, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import useQueryParams from '~/hooks/useQuery'

import { useCampaignForm } from '../components/CampaignForm/hooks/useCampaignForm'
import {
  listSubpublishersKey,
  listSubpublishersService
} from '../services/publisher/subpublishers'

interface UseSubpublishersProps {
  publisher?: PublisherId
  name?: string
  campaignId?: CampaignId
  matchedOnly?: boolean
}

export function useSubpublishers({
  publisher,
  name,
  campaignId,
  matchedOnly
}: UseSubpublishersProps) {
  const {
    allowManageSubpublishers,
    selectedSubpublishers,
    setSelectedSubpublishers,
    setAllSubpublishers
  } = useCampaignForm()

  const params = useQueryParams()

  /**
   * Get subpublishers from publisher
   */
  const publisherId = publisher || (params.get('rmid') as PublisherId)

  const { data, isPending } = useQuery({
    queryKey: [
      listSubpublishersKey,
      publisherId,
      name,
      campaignId && matchedOnly ? campaignId : null
    ],
    queryFn: async () => {
      const response = await listSubpublishersService({
        params: {
          publisher_id: publisherId,
          quantity: 250,
          name: name || undefined,
          ...(campaignId && matchedOnly
            ? { campaign_id: campaignId, matched_only: true }
            : {})
        }
      })

      return response.data
    }
  })

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked, value } = e.currentTarget
      const subpublisherId = value as PublisherId

      const payload = checked
        ? [...selectedSubpublishers, subpublisherId]
        : selectedSubpublishers.filter(item => item !== subpublisherId)

      setSelectedSubpublishers(payload)
    },
    [selectedSubpublishers, setSelectedSubpublishers]
  )

  useEffect(() => {
    if (!!data && !!setAllSubpublishers) {
      setAllSubpublishers(
        data?.data?.map(item => ({
          id: item?.id,
          name: item?.name
        })) || []
      )
    }
  }, [data, setAllSubpublishers])

  return {
    data,
    isPending,
    handleChange,
    allowManageSubpublishers
  }
}
